// src/components/AddBlog.js

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as BackIcon } from "../icons/back.svg";
import "../style/AddProduct.css";
import { db, storage } from "../firebase-config";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadString, getDownloadURL } from "firebase/storage";

const AddBlog = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [content, setContent] = useState("");
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [initialPostData, setInitialPostData] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isUpdateValid, setIsUpdateValid] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load existing post data if editing
  useEffect(() => {
    if (location.state?.post) {
      const postData = location.state.post;
      setIsEditing(true);
      setInitialPostData(postData);
      setContent(postData.content || "");
      setImages(postData.images || []);
    }
  }, [location.state]);

  // Validate form
  useEffect(() => {
    const isValid = content.trim() !== "";
    setIsFormValid(isValid);
  }, [content]);

  // Check for changes (for update button)
  useEffect(() => {
    if (!initialPostData) return;

    const hasChanges =
      content !== (initialPostData.content || "") ||
      JSON.stringify(images) !== JSON.stringify(initialPostData.images || []);

    setIsUpdateValid(hasChanges && content.trim() !== "");
  }, [content, images, initialPostData]);

  const handleBackClick = () => {
    if (location.state?.from) {
      navigate(location.state.from, {
        state: { activeComponent: location.state.activeComponent },
      });
    } else {
      navigate("/mc-admin/panel", {
        state: { activeComponent: "blogManagement" },
      });
    }
  };

  const handleAddImages = (event) => {
    const files = event.target.files;
    if (images.length + files.length > 4) {
      showNotification("Maximum 4 images allowed");
      return;
    }

    const newImages = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const reader = new FileReader();
      reader.onload = (e) => {
        newImages.push(e.target.result);
        if (newImages.length === files.length) {
          setImages((prevImages) => [...prevImages, ...newImages].slice(0, 4));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, idx) => idx !== index);
    setImages(newImages);
  };

  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(""), 3000);
  };

  const handleUpload = async () => {
    setIsLoading(true);
    try {
      const uploadImage = async (image, path) => {
        const storageRef = ref(storage, path);
        await uploadString(storageRef, image, "data_url");
        return getDownloadURL(storageRef);
      };

      const imageUrls = await Promise.all(
        images
          .filter((img) => img.startsWith("data:"))
          .map((image, index) =>
            uploadImage(image, `designJournal/${Date.now()}-${index}`),
          ),
      );

      const existingUrls = images.filter((img) => !img.startsWith("data:"));
      const allImageUrls = [...existingUrls, ...imageUrls];

      const postData = {
        content: content.trim(),
        images: allImageUrls,
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, "designJournal"), postData);

      showNotification("Blog post created successfully!");
      setContent("");
      setImages([]);

      setTimeout(() => handleBackClick(), 1500);
    } catch (error) {
      console.error("Error creating blog post:", error);
      showNotification("Failed to create blog post. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    setIsLoading(true);
    try {
      const postId = initialPostData.id;

      const uploadImage = async (image, path) => {
        const storageRef = ref(storage, path);
        await uploadString(storageRef, image, "data_url");
        return getDownloadURL(storageRef);
      };

      const newImageUrls = await Promise.all(
        images
          .filter((img) => img.startsWith("data:"))
          .map((image, index) =>
            uploadImage(image, `designJournal/${Date.now()}-${index}`),
          ),
      );

      const existingUrls = images.filter((img) => !img.startsWith("data:"));
      const allImageUrls = [...existingUrls, ...newImageUrls];

      const postData = {
        content: content.trim(),
        images: allImageUrls,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "designJournal", postId), postData);

      showNotification("Blog post updated successfully!");
      setTimeout(() => handleBackClick(), 1500);
    } catch (error) {
      console.error("Error updating blog post:", error);
      showNotification("Failed to update blog post. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const postId = initialPostData.id;
      await deleteDoc(doc(db, "designJournal", postId));

      showNotification("Blog post deleted successfully!");
      setTimeout(() => handleBackClick(), 1500);
    } catch (error) {
      console.error("Error deleting blog post:", error);
      showNotification("Failed to delete blog post. Please try again.");
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="add-product-container-unique">
      <div className="add-product-header-unique">
        <div className="back-and-title-container">
          <div className="back-button-unique" onClick={handleBackClick}>
            <BackIcon className="back-icon-unique" />
            <span>Back</span>
          </div>
          <span className="add-product-title-unique">
            {isEditing ? "Edit Blog Post" : "Add Blog Post"}
          </span>
        </div>
        <div className="upload-button-container-unique">
          {isEditing && (
            <div
              className="upload-button-unique"
              style={{
                backgroundColor: "#ff4444",
                marginRight: "10px",
              }}
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete Post
            </div>
          )}

          {!isEditing && (
            <div
              className={`upload-button-unique ${
                !isFormValid || isLoading ? "disabled" : ""
              }`}
              onClick={isFormValid && !isLoading ? handleUpload : null}
            >
              {isLoading ? <span className="loader"></span> : "Publish Post"}
            </div>
          )}

          {isEditing && (
            <div
              className={`upload-button-unique ${
                !isUpdateValid || isLoading ? "disabled" : ""
              }`}
              onClick={isUpdateValid && !isLoading ? handleUpdate : null}
            >
              {isLoading ? <span className="loader"></span> : "Update Post"}
            </div>
          )}
        </div>
      </div>

      {/* Two column layout: Images left, Textarea right */}
      <div
        style={{
          display: "flex",
          gap: "20px",
          padding: "20px",
          height: "calc(100vh - 120px)",
        }}
      >
        {/* Images Section - Left */}
        <div style={{ width: "300px", flexShrink: 0 }}>
          <div className="new-section-title">Images (Max 4)</div>
          <div
            style={{
              backgroundColor: "white",
              padding: "15px",
              borderRadius: "4px",
            }}
          >
            <label
              className="add-files-link-unique"
              style={{ marginBottom: "15px", display: "block" }}
            >
              Click here to add images
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handleAddImages}
                disabled={images.length >= 4}
              />
            </label>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "10px",
              }}
            >
              {images.map((image, index) => (
                <div
                  key={index}
                  style={{
                    position: "relative",
                    aspectRatio: "1",
                    backgroundColor: "#f0f0f0",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={image}
                    alt={`Upload ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    onClick={() => handleRemoveImage(index)}
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      width: "24px",
                      height: "24px",
                      backgroundColor: "rgba(255,0,0,0.8)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  >
                    ×
                  </div>
                </div>
              ))}

              {Array.from({ length: 4 - images.length }).map((_, index) => (
                <div
                  key={`empty-${index}`}
                  style={{
                    aspectRatio: "1",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                    border: "2px dashed #ddd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#999",
                    fontSize: "12px",
                  }}
                >
                  Empty
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Textarea - Takes all remaining space */}
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="My Blog content here..."
          style={{
            flex: 1,
            padding: "20px",
            fontSize: "14px",
            fontFamily: "Georgia, serif",
            border: "1px solid #ddd",
            borderRadius: "4px",
            resize: "none",
            boxSizing: "border-box",
            lineHeight: "1.8",
            overflowY: "auto",
            outline: "none",
          }}
        />
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "8px",
              maxWidth: "400px",
              textAlign: "center",
            }}
          >
            <h3 style={{ marginBottom: "20px" }}>Delete Blog Post?</h3>
            <p style={{ marginBottom: "30px", color: "#666" }}>
              Are you sure you want to delete this blog post? This action cannot
              be undone.
            </p>
            <div
              style={{ display: "flex", gap: "10px", justifyContent: "center" }}
            >
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#ccc",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isLoading}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#ff4444",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: isLoading ? "not-allowed" : "pointer",
                }}
              >
                {isLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {notification && (
        <div className={`notification ${notification ? "show" : ""}`}>
          {notification}
        </div>
      )}
    </div>
  );
};

export default AddBlog;
